import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  TrustpilotSettings,
  TrustpilotStats,
  TrustpilotHistoryResponse,
  UpdateTrustpilotSettingsDto,
  SendInvitationDto,
  getTrustpilotSettings as getTrustpilotSettingsAction,
  updateTrustpilotSettings as updateTrustpilotSettingsAction,
  getTrustpilotStats as getTrustpilotStatsAction,
  getTrustpilotHistory as getTrustpilotHistoryAction,
  sendTrustpilotInvitation as sendTrustpilotInvitationAction,
  triggerTrustpilotScheduler as triggerTrustpilotSchedulerAction,
} from "../Actions/TrustpilotActions";

// Async Thunks
export const fetchTrustpilotSettings = createAsyncThunk(
  "trustpilot/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      return await getTrustpilotSettingsAction();
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch Trustpilot settings");
    }
  }
);

export const updateTrustpilotSettings = createAsyncThunk(
  "trustpilot/updateSettings",
  async (data: UpdateTrustpilotSettingsDto, { rejectWithValue }) => {
    try {
      return await updateTrustpilotSettingsAction(data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to update Trustpilot settings");
    }
  }
);

export const fetchTrustpilotStats = createAsyncThunk(
  "trustpilot/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      return await getTrustpilotStatsAction();
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch Trustpilot statistics");
    }
  }
);

export const fetchTrustpilotHistory = createAsyncThunk(
  "trustpilot/fetchHistory",
  async ({ page, limit }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await getTrustpilotHistoryAction(page, limit);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch Trustpilot history");
    }
  }
);

export const sendTrustpilotInvitation = createAsyncThunk(
  "trustpilot/sendInvitation",
  async (data: SendInvitationDto, { rejectWithValue }) => {
    try {
      return await sendTrustpilotInvitationAction(data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to send invitation");
    }
  }
);

export const triggerTrustpilotScheduler = createAsyncThunk(
  "trustpilot/triggerScheduler",
  async (_, { rejectWithValue }) => {
    try {
      return await triggerTrustpilotSchedulerAction();
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to trigger scheduler");
    }
  }
);

// State interface
interface TrustpilotState {
  settings: TrustpilotSettings | null;
  settingsLoading: boolean;
  settingsError: string | null;

  stats: TrustpilotStats | null;
  statsLoading: boolean;
  statsError: string | null;

  history: TrustpilotHistoryResponse | null;
  historyLoading: boolean;
  historyError: string | null;

  actionLoading: boolean;
  actionError: string | null;
  actionSuccess: string | null;
}

const initialState: TrustpilotState = {
  settings: null,
  settingsLoading: false,
  settingsError: null,

  stats: null,
  statsLoading: false,
  statsError: null,

  history: null,
  historyLoading: false,
  historyError: null,

  actionLoading: false,
  actionError: null,
  actionSuccess: null,
};

const trustpilotSlice = createSlice({
  name: "trustpilot",
  initialState,
  reducers: {
    clearActionMessages: (state) => {
      state.actionError = null;
      state.actionSuccess = null;
    },
    clearErrors: (state) => {
      state.settingsError = null;
      state.statsError = null;
      state.historyError = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Settings
    builder.addCase(fetchTrustpilotSettings.pending, (state) => {
      state.settingsLoading = true;
      state.settingsError = null;
    });
    builder.addCase(fetchTrustpilotSettings.fulfilled, (state, action) => {
      state.settingsLoading = false;
      state.settings = action.payload;
    });
    builder.addCase(fetchTrustpilotSettings.rejected, (state, action) => {
      state.settingsLoading = false;
      state.settingsError = action.payload as string;
    });

    // Update Settings
    builder.addCase(updateTrustpilotSettings.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
      state.actionSuccess = null;
    });
    builder.addCase(updateTrustpilotSettings.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.settings = action.payload;
      state.actionSuccess = "Settings saved successfully!";
    });
    builder.addCase(updateTrustpilotSettings.rejected, (state, action) => {
      state.actionLoading = false;
      state.actionError = action.payload as string;
    });

    // Fetch Stats
    builder.addCase(fetchTrustpilotStats.pending, (state) => {
      state.statsLoading = true;
      state.statsError = null;
    });
    builder.addCase(fetchTrustpilotStats.fulfilled, (state, action) => {
      state.statsLoading = false;
      state.stats = action.payload;
    });
    builder.addCase(fetchTrustpilotStats.rejected, (state, action) => {
      state.statsLoading = false;
      state.statsError = action.payload as string;
    });

    // Fetch History
    builder.addCase(fetchTrustpilotHistory.pending, (state) => {
      state.historyLoading = true;
      state.historyError = null;
    });
    builder.addCase(fetchTrustpilotHistory.fulfilled, (state, action) => {
      state.historyLoading = false;
      state.history = action.payload;
    });
    builder.addCase(fetchTrustpilotHistory.rejected, (state, action) => {
      state.historyLoading = false;
      state.historyError = action.payload as string;
    });

    // Send Invitation
    builder.addCase(sendTrustpilotInvitation.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
      state.actionSuccess = null;
    });
    builder.addCase(sendTrustpilotInvitation.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.actionSuccess = action.payload.message;
      // Clear stats and history to force refresh
      state.stats = null;
      state.history = null;
    });
    builder.addCase(sendTrustpilotInvitation.rejected, (state, action) => {
      state.actionLoading = false;
      state.actionError = action.payload as string;
    });

    // Trigger Scheduler
    builder.addCase(triggerTrustpilotScheduler.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
      state.actionSuccess = null;
    });
    builder.addCase(triggerTrustpilotScheduler.fulfilled, (state, action) => {
      state.actionLoading = false;
      state.actionSuccess = action.payload.message;
      // Clear stats and history to force refresh
      state.stats = null;
      state.history = null;
    });
    builder.addCase(triggerTrustpilotScheduler.rejected, (state, action) => {
      state.actionLoading = false;
      state.actionError = action.payload as string;
    });
  },
});

export const { clearActionMessages, clearErrors } = trustpilotSlice.actions;
export default trustpilotSlice.reducer;

